import React, { useState, useEffect } from 'react';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

function AdminTranslations() {
  var i18n = useI18n();
  var t = i18n.t;

  var statsState = useState(null);
  var stats = statsState[0];
  var setStats = statsState[1];

  var translationsState = useState([]);
  var translations = translationsState[0];
  var setTranslations = translationsState[1];

  var missingState = useState(null);
  var missing = missingState[0];
  var setMissing = missingState[1];

  var attentionState = useState(null);
  var attention = attentionState[0];
  var setAttention = attentionState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var generatingState = useState(false);
  var generating = generatingState[0];
  var setGenerating = generatingState[1];

  var filterState = useState('needs_review');
  var filter = filterState[0];
  var setFilter = filterState[1];

  var tabState = useState('attention');
  var activeTab = tabState[0];
  var setActiveTab = tabState[1];

  var editingState = useState(null);
  var editing = editingState[0];
  var setEditing = editingState[1];

  var editTextState = useState('');
  var editText = editTextState[0];
  var setEditText = editTextState[1];

  useEffect(function() {
    loadData();
  }, [filter]);

  useEffect(function() {
    if (activeTab === 'missing') {
      loadMissing();
    } else if (activeTab === 'attention') {
      loadAttention();
    }
  }, [activeTab]);

  function loadData() {
    setLoading(true);
    Promise.all([
      api.get('/admin/translations/stats'),
      api.get('/admin/translations/review?status=' + filter)
    ])
    .then(function(results) {
      setStats(results[0]);
      setTranslations(results[1].translations);
    })
    .catch(function(err) {
      console.error('Failed to load translations:', err);
    })
    .finally(function() {
      setLoading(false);
    });
  }

  function loadMissing() {
    setLoading(true);
    api.get('/admin/translations/missing')
      .then(function(result) {
        setMissing(result);
      })
      .catch(function(err) {
        console.error('Failed to load missing translations:', err);
      })
      .finally(function() {
        setLoading(false);
      });
  }

  function loadAttention() {
    setLoading(true);
    api.get('/admin/translations/attention?qa_threshold=85')
      .then(function(result) {
        setAttention(result);
      })
      .catch(function(err) {
        console.error('Failed to load attention translations:', err);
      })
      .finally(function() {
        setLoading(false);
      });
  }

  function handleApprove(id, customText) {
    var body = customText ? { edited_text: customText } : {};
    api.put('/admin/translations/' + id + '/approve', body)
      .then(function() {
        setEditing(null);
        loadData();
        if (activeTab === 'attention') loadAttention();
      })
      .catch(function(err) {
        alert('Failed to approve: ' + err.message);
      });
  }

  function handleReview(id, action, editedText) {
    var body = { action: action };
    if (editedText) body.edited_text = editedText;

    api.put('/admin/translations/' + id + '/review', body)
      .then(function() {
        setEditing(null);
        loadAttention();
      })
      .catch(function(err) {
        alert('Failed to review: ' + err.message);
      });
  }

  function handleDelete(id) {
    if (!window.confirm('Delete this translation? It will need to be regenerated.')) return;
    api.delete('/admin/translations/' + id)
      .then(function() {
        loadData();
      })
      .catch(function(err) {
        alert('Failed to delete: ' + err.message);
      });
  }

  function handleRetry(id) {
    api.post('/admin/translations/' + id + '/retry')
      .then(function() {
        alert('Translation queued for retry');
        loadData();
      })
      .catch(function(err) {
        alert('Failed to retry: ' + err.message);
      });
  }

  function handleApproveAll() {
    if (!window.confirm('Approve ALL translations needing review?')) return;
    api.post('/admin/translations/approve-all')
      .then(function(result) {
        alert('Approved ' + result.count + ' translations');
        loadData();
      })
      .catch(function(err) {
        alert('Failed: ' + err.message);
      });
  }

  function handleGenerateOne(entityType, entityId, fields) {
    setGenerating(true);
    api.post('/admin/translations/generate', {
      entity_type: entityType,
      entity_id: entityId,
      fields: fields
    })
    .then(function(result) {
      var successCount = result.results.filter(function(r) { return r.status === 'approved'; }).length;
      alert('Generated ' + successCount + ' of ' + result.results.length + ' translations');
      loadMissing();
    })
    .catch(function(err) {
      alert('Failed to generate: ' + err.message);
    })
    .finally(function() {
      setGenerating(false);
    });
  }

  function handleGenerateAll() {
    if (!window.confirm('Generate ALL missing translations? This may take a while.')) return;
    setGenerating(true);
    api.post('/admin/translations/generate-all-missing')
      .then(function(result) {
        alert('Generated ' + result.total + ' translations');
        loadMissing();
      })
      .catch(function(err) {
        alert('Failed: ' + err.message);
      })
      .finally(function() {
        setGenerating(false);
      });
  }

  function startEdit(trans) {
    setEditing(trans.id);
    setEditText(trans.translated_text || '');
  }

  function formatEntityType(type) {
    return type.replace(/_/g, ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); });
  }

  function getQABadgeColor(score) {
    if (score >= 90) return '#22c55e';
    if (score >= 80) return '#f59e0b';
    return '#ef4444';
  }

  function getAttentionBadge(reason) {
    if (reason === 'low_qa') return { color: '#f59e0b', text: t('translation.lowConfidence') };
    if (reason === 'vendor_edited') return { color: '#3b82f6', text: 'Vendor Edited' };
    if (reason === 'owner_edited') return { color: '#8b5cf6', text: 'Owner Edited' };
    return { color: '#6b7280', text: reason };
  }

  if (loading && !stats && !missing && !attention) {
    return React.createElement('div', { className: 'container', style: { paddingTop: '20px' } },
      'Loading...'
    );
  }

  // Calculate attention count for badge
  var attentionCount = attention ? (attention.summary.low_qa_count + attention.summary.edited_count) : 0;

  // Tab buttons
  var tabButtons = React.createElement('div', {
    className: 'flex gap-2 mb-4',
    style: { borderBottom: '1px solid var(--border)', paddingBottom: '12px' }
  },
    React.createElement('button', {
      className: 'btn ' + (activeTab === 'attention' ? 'btn-primary' : 'btn-secondary'),
      onClick: function() { setActiveTab('attention'); }
    }, 'Needs Attention',
      attentionCount > 0 && React.createElement('span', {
        style: {
          marginLeft: '8px',
          background: '#f59e0b',
          color: 'white',
          padding: '2px 6px',
          borderRadius: '10px',
          fontSize: '12px'
        }
      }, attentionCount)
    ),
    React.createElement('button', {
      className: 'btn ' + (activeTab === 'review' ? 'btn-primary' : 'btn-secondary'),
      onClick: function() { setActiveTab('review'); }
    }, 'Review Queue'),
    React.createElement('button', {
      className: 'btn ' + (activeTab === 'missing' ? 'btn-primary' : 'btn-secondary'),
      onClick: function() { setActiveTab('missing'); }
    }, 'Missing Translations',
      missing && missing.total > 0 && React.createElement('span', {
        style: {
          marginLeft: '8px',
          background: '#ef4444',
          color: 'white',
          padding: '2px 6px',
          borderRadius: '10px',
          fontSize: '12px'
        }
      }, missing.total)
    )
  );

  // Attention tab - Low QA and Edited translations
  if (activeTab === 'attention') {
    var allAttentionItems = [];
    if (attention) {
      allAttentionItems = [
        ...attention.low_qa.map(function(item) { return Object.assign({}, item, { _section: 'low_qa' }); }),
        ...attention.edited.map(function(item) { return Object.assign({}, item, { _section: 'edited' }); })
      ];
    }

    return React.createElement('div', null,
      React.createElement('div', { className: 'flex justify-between items-center mb-4' },
        React.createElement('h2', { style: { fontSize: '20px', fontWeight: '600' } }, 'Translations Needing Attention')
      ),
      tabButtons,

      loading
        ? React.createElement('div', { className: 'card text-center' }, 'Loading...')
        : !attention || allAttentionItems.length === 0
          ? React.createElement('div', { className: 'card text-center text-secondary' },
              'All translations look good! No low-confidence or edited translations found.'
            )
          : React.createElement('div', null,
              // Summary
              React.createElement('div', { className: 'flex gap-3 mb-4' },
                React.createElement('div', {
                  className: 'card',
                  style: { flex: '1', textAlign: 'center', background: '#fef3c7' }
                },
                  React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold', color: '#d97706' } },
                    attention.summary.low_qa_count),
                  React.createElement('div', { style: { fontSize: '13px', color: '#92400e' } },
                    'Low Confidence (QA < ' + attention.summary.qa_threshold + ')')
                ),
                React.createElement('div', {
                  className: 'card',
                  style: { flex: '1', textAlign: 'center', background: '#dbeafe' }
                },
                  React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold', color: '#2563eb' } },
                    attention.summary.edited_count),
                  React.createElement('div', { style: { fontSize: '13px', color: '#1e40af' } },
                    'Vendor/Owner Edited')
                )
              ),

              // Low QA translations
              attention.low_qa.length > 0 && React.createElement('div', { className: 'mb-4' },
                React.createElement('h3', {
                  style: { fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#d97706' }
                }, t('translation.lowConfidence') + ' Translations'),
                attention.low_qa.map(function(trans) {
                  var isEditing = editing === trans.id;
                  return React.createElement('div', {
                    key: trans.id,
                    className: 'card mb-3',
                    style: { padding: '16px', borderLeft: '4px solid #f59e0b' }
                  },
                    // Header
                    React.createElement('div', { className: 'flex justify-between items-start mb-2' },
                      React.createElement('div', null,
                        React.createElement('span', {
                          className: 'badge',
                          style: { background: 'var(--primary)', color: 'white', marginRight: '8px' }
                        }, formatEntityType(trans.entity_type)),
                        React.createElement('span', { className: 'text-secondary text-sm' },
                          trans.entity_name || trans.entity_id.substring(0, 8)
                        )
                      ),
                      React.createElement('span', {
                        className: 'badge',
                        style: { background: getQABadgeColor(trans.qa_score), color: 'white' }
                      }, 'QA: ' + trans.qa_score)
                    ),

                    // Source text (English)
                    React.createElement('div', { className: 'mb-2' },
                      React.createElement('div', { className: 'text-secondary text-xs mb-1' }, 'Original (English):'),
                      React.createElement('div', {
                        style: {
                          background: 'var(--background)',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }
                      }, trans.source_text)
                    ),

                    // Translation
                    React.createElement('div', { className: 'mb-3' },
                      React.createElement('div', { className: 'text-secondary text-xs mb-1' },
                        'Translation (' + (trans.lang_code || 'TH').toUpperCase() + '):'),
                      isEditing
                        ? React.createElement('textarea', {
                            value: editText,
                            onChange: function(e) { setEditText(e.target.value); },
                            style: {
                              width: '100%',
                              minHeight: '80px',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid var(--border)',
                              fontSize: '14px'
                            }
                          })
                        : React.createElement('div', {
                            style: {
                              background: '#fef3c7',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }
                          }, trans.translated_text)
                    ),

                    // Actions
                    React.createElement('div', { className: 'flex gap-2 justify-end' },
                      isEditing
                        ? React.createElement(React.Fragment, null,
                            React.createElement('button', {
                              className: 'btn btn-secondary',
                              onClick: function() { setEditing(null); }
                            }, 'Cancel'),
                            React.createElement('button', {
                              className: 'btn btn-primary',
                              onClick: function() { handleReview(trans.id, 'edit', editText); }
                            }, 'Save & Approve')
                          )
                        : React.createElement(React.Fragment, null,
                            React.createElement('button', {
                              className: 'btn btn-secondary',
                              onClick: function() { handleReview(trans.id, 'revert'); }
                            }, 'Regenerate'),
                            React.createElement('button', {
                              className: 'btn btn-secondary',
                              onClick: function() { startEdit(trans); }
                            }, 'Edit'),
                            React.createElement('button', {
                              className: 'btn btn-primary',
                              onClick: function() { handleReview(trans.id, 'approve'); }
                            }, 'Approve As-Is')
                          )
                    )
                  );
                })
              ),

              // Vendor/Owner edited translations
              attention.edited.length > 0 && React.createElement('div', { className: 'mb-4' },
                React.createElement('h3', {
                  style: { fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#2563eb' }
                }, 'Vendor/Owner Edited Translations'),
                attention.edited.map(function(trans) {
                  var isEditing = editing === trans.id;
                  var badge = getAttentionBadge(trans.attention_reason);
                  return React.createElement('div', {
                    key: trans.id,
                    className: 'card mb-3',
                    style: { padding: '16px', borderLeft: '4px solid #3b82f6' }
                  },
                    // Header
                    React.createElement('div', { className: 'flex justify-between items-start mb-2' },
                      React.createElement('div', null,
                        React.createElement('span', {
                          className: 'badge',
                          style: { background: 'var(--primary)', color: 'white', marginRight: '8px' }
                        }, formatEntityType(trans.entity_type)),
                        React.createElement('span', { className: 'text-secondary text-sm' },
                          trans.entity_name || trans.entity_id.substring(0, 8)
                        )
                      ),
                      React.createElement('span', {
                        className: 'badge',
                        style: { background: badge.color, color: 'white' }
                      }, badge.text)
                    ),

                    // Source text (English)
                    React.createElement('div', { className: 'mb-2' },
                      React.createElement('div', { className: 'text-secondary text-xs mb-1' }, 'Original (English):'),
                      React.createElement('div', {
                        style: {
                          background: 'var(--background)',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '14px'
                        }
                      }, trans.source_text)
                    ),

                    // Current Translation (edited by vendor/owner)
                    React.createElement('div', { className: 'mb-3' },
                      React.createElement('div', { className: 'text-secondary text-xs mb-1' },
                        'Current Translation (edited):'),
                      isEditing
                        ? React.createElement('textarea', {
                            value: editText,
                            onChange: function(e) { setEditText(e.target.value); },
                            style: {
                              width: '100%',
                              minHeight: '80px',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: '1px solid var(--border)',
                              fontSize: '14px'
                            }
                          })
                        : React.createElement('div', {
                            style: {
                              background: '#dbeafe',
                              padding: '8px 12px',
                              borderRadius: '6px',
                              fontSize: '14px'
                            }
                          }, trans.translated_text)
                    ),

                    // Actions
                    React.createElement('div', { className: 'flex gap-2 justify-end' },
                      isEditing
                        ? React.createElement(React.Fragment, null,
                            React.createElement('button', {
                              className: 'btn btn-secondary',
                              onClick: function() { setEditing(null); }
                            }, 'Cancel'),
                            React.createElement('button', {
                              className: 'btn btn-primary',
                              onClick: function() { handleReview(trans.id, 'edit', editText); }
                            }, 'Save & Approve')
                          )
                        : React.createElement(React.Fragment, null,
                            React.createElement('button', {
                              className: 'btn btn-secondary',
                              onClick: function() { handleReview(trans.id, 'revert'); }
                            }, 'Revert to AI'),
                            React.createElement('button', {
                              className: 'btn btn-secondary',
                              onClick: function() { startEdit(trans); }
                            }, 'Edit'),
                            React.createElement('button', {
                              className: 'btn btn-primary',
                              onClick: function() { handleReview(trans.id, 'approve'); }
                            }, 'Approve Edit')
                          )
                    )
                  );
                })
              ),

              // Recent edit audit log
              attention.audit_log && attention.audit_log.length > 0 && React.createElement('div', { className: 'mb-4' },
                React.createElement('h3', {
                  style: { fontSize: '16px', fontWeight: '600', marginBottom: '12px' }
                }, 'Recent Edit History'),
                React.createElement('div', { className: 'card', style: { padding: '12px' } },
                  React.createElement('table', { style: { width: '100%', fontSize: '13px' } },
                    React.createElement('thead', null,
                      React.createElement('tr', { style: { borderBottom: '1px solid var(--border)' } },
                        React.createElement('th', { style: { textAlign: 'left', padding: '8px' } }, 'Editor'),
                        React.createElement('th', { style: { textAlign: 'left', padding: '8px' } }, 'Field'),
                        React.createElement('th', { style: { textAlign: 'left', padding: '8px' } }, 'Original'),
                        React.createElement('th', { style: { textAlign: 'left', padding: '8px' } }, 'New'),
                        React.createElement('th', { style: { textAlign: 'left', padding: '8px' } }, 'Date')
                      )
                    ),
                    React.createElement('tbody', null,
                      attention.audit_log.slice(0, 10).map(function(log, idx) {
                        return React.createElement('tr', {
                          key: idx,
                          style: { borderBottom: '1px solid var(--border)' }
                        },
                          React.createElement('td', { style: { padding: '8px' } },
                            React.createElement('span', {
                              className: 'badge',
                              style: {
                                background: log.editor_type === 'vendor' ? '#3b82f6' : '#8b5cf6',
                                color: 'white',
                                fontSize: '11px'
                              }
                            }, log.editor_type),
                            ' ',
                            log.editor_name || 'Unknown'
                          ),
                          React.createElement('td', { style: { padding: '8px' } },
                            formatEntityType(log.entity_type)),
                          React.createElement('td', { style: { padding: '8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' } },
                            (log.original_text || '').substring(0, 50) + '...'),
                          React.createElement('td', { style: { padding: '8px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' } },
                            (log.edited_text || '').substring(0, 50) + '...'),
                          React.createElement('td', { style: { padding: '8px' } },
                            new Date(log.created_at).toLocaleDateString())
                        );
                      })
                    )
                  )
                )
              )
            )
    );
  }

  // Missing translations tab
  if (activeTab === 'missing') {
    return React.createElement('div', null,
      React.createElement('div', { className: 'flex justify-between items-center mb-4' },
        React.createElement('h2', { style: { fontSize: '20px', fontWeight: '600' } }, 'Missing Translations')
      ),
      tabButtons,

      loading
        ? React.createElement('div', { className: 'card text-center' }, 'Loading...')
        : !missing || missing.total === 0
          ? React.createElement('div', { className: 'card text-center text-secondary' },
              'All content has translations!'
            )
          : React.createElement('div', null,
              React.createElement('div', { className: 'mb-4' },
                React.createElement('button', {
                  className: 'btn btn-primary',
                  disabled: generating,
                  onClick: handleGenerateAll
                }, generating ? 'Generating...' : 'Generate All Missing (' + missing.total + ')')
              ),

              missing.offers.length > 0 && React.createElement('div', { className: 'mb-4' },
                React.createElement('h3', { style: { fontSize: '16px', fontWeight: '600', marginBottom: '12px' } },
                  'Offers (' + missing.offers.length + ')'),
                missing.offers.map(function(item) {
                  return React.createElement('div', {
                    key: item.id,
                    className: 'card mb-2',
                    style: { padding: '12px' }
                  },
                    React.createElement('div', { className: 'flex justify-between items-center' },
                      React.createElement('div', null,
                        React.createElement('div', { style: { fontWeight: '500' } }, item.name),
                        React.createElement('div', { className: 'text-secondary text-sm' },
                          'Missing: ' + item.missing_fields.join(', ')
                        )
                      ),
                      React.createElement('button', {
                        className: 'btn btn-secondary btn-sm',
                        disabled: generating,
                        onClick: function() { handleGenerateOne(item.type, item.id, item.missing_fields); }
                      }, 'Generate')
                    )
                  );
                })
              ),

              missing.properties.length > 0 && React.createElement('div', { className: 'mb-4' },
                React.createElement('h3', { style: { fontSize: '16px', fontWeight: '600', marginBottom: '12px' } },
                  'Properties (' + missing.properties.length + ')'),
                missing.properties.map(function(item) {
                  return React.createElement('div', {
                    key: item.id,
                    className: 'card mb-2',
                    style: { padding: '12px' }
                  },
                    React.createElement('div', { className: 'flex justify-between items-center' },
                      React.createElement('div', null,
                        React.createElement('div', { style: { fontWeight: '500' } }, item.name),
                        React.createElement('div', { className: 'text-secondary text-sm' },
                          'Missing: ' + item.missing_fields.join(', ')
                        )
                      ),
                      React.createElement('button', {
                        className: 'btn btn-secondary btn-sm',
                        disabled: generating,
                        onClick: function() { handleGenerateOne(item.type, item.id, item.missing_fields); }
                      }, 'Generate')
                    )
                  );
                })
              ),

              missing.categories.length > 0 && React.createElement('div', { className: 'mb-4' },
                React.createElement('h3', { style: { fontSize: '16px', fontWeight: '600', marginBottom: '12px' } },
                  'Categories (' + missing.categories.length + ')'),
                missing.categories.map(function(item) {
                  return React.createElement('div', {
                    key: item.id,
                    className: 'card mb-2',
                    style: { padding: '12px' }
                  },
                    React.createElement('div', { className: 'flex justify-between items-center' },
                      React.createElement('div', null,
                        React.createElement('div', { style: { fontWeight: '500' } }, item.name),
                        React.createElement('div', { className: 'text-secondary text-sm' },
                          'Missing: ' + item.missing_fields.join(', ')
                        )
                      ),
                      React.createElement('button', {
                        className: 'btn btn-secondary btn-sm',
                        disabled: generating,
                        onClick: function() { handleGenerateOne(item.type, item.id, item.missing_fields); }
                      }, 'Generate')
                    )
                  );
                })
              )
            )
    );
  }

  // Review tab (original content)
  return React.createElement('div', null,
    React.createElement('div', { className: 'flex justify-between items-center mb-4' },
      React.createElement('h2', { style: { fontSize: '20px', fontWeight: '600' } }, 'Translation Review'),
      filter === 'needs_review' && translations.length > 0 &&
        React.createElement('button', {
          className: 'btn btn-primary',
          onClick: handleApproveAll
        }, 'Approve All (' + translations.length + ')')
    ),
    tabButtons,

    stats && React.createElement('div', { className: 'flex gap-3 mb-4', style: { flexWrap: 'wrap' } },
      stats.byStatus.map(function(s) {
        return React.createElement('div', {
          key: s.status,
          className: 'card',
          style: { flex: '1', minWidth: '120px', textAlign: 'center', cursor: 'pointer',
                   background: filter === s.status ? 'var(--primary)' : 'var(--surface)',
                   color: filter === s.status ? 'white' : 'var(--text)' },
          onClick: function() { setFilter(s.status); }
        },
          React.createElement('div', { style: { fontSize: '24px', fontWeight: 'bold' } }, s.count),
          React.createElement('div', { style: { fontSize: '13px', textTransform: 'capitalize' } },
            s.status.replace(/_/g, ' ')
          )
        );
      })
    ),

    loading
      ? React.createElement('div', { className: 'card text-center' }, 'Loading...')
      : translations.length === 0
        ? React.createElement('div', { className: 'card text-center text-secondary' },
            'No translations with status: ' + filter.replace(/_/g, ' ')
          )
        : translations.map(function(trans) {
            var isEditing = editing === trans.id;

            return React.createElement('div', {
              key: trans.id,
              className: 'card mb-3',
              style: { padding: '16px' }
            },
              React.createElement('div', { className: 'flex justify-between items-start mb-2' },
                React.createElement('div', null,
                  React.createElement('span', {
                    className: 'badge',
                    style: { background: 'var(--primary)', color: 'white', marginRight: '8px' }
                  }, formatEntityType(trans.entity_type)),
                  React.createElement('span', { className: 'text-secondary text-sm' },
                    trans.entity_name || trans.entity_id.substring(0, 8)
                  )
                ),
                React.createElement('div', { className: 'flex gap-2' },
                  trans.qa_score && React.createElement('span', {
                    className: 'badge',
                    style: {
                      background: trans.qa_score >= 85 ? '#22c55e' : trans.qa_score >= 70 ? '#f59e0b' : '#ef4444',
                      color: 'white'
                    }
                  }, 'QA: ' + trans.qa_score)
                )
              ),

              React.createElement('div', { className: 'mb-2' },
                React.createElement('div', { className: 'text-secondary text-xs mb-1' }, 'Source (English):'),
                React.createElement('div', {
                  style: {
                    background: 'var(--background)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }
                }, trans.source_text)
              ),

              React.createElement('div', { className: 'mb-3' },
                React.createElement('div', { className: 'text-secondary text-xs mb-1' },
                  'Translation (' + trans.lang_code.toUpperCase() + '):'),
                isEditing
                  ? React.createElement('textarea', {
                      value: editText,
                      onChange: function(e) { setEditText(e.target.value); },
                      style: {
                        width: '100%',
                        minHeight: '80px',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid var(--border)',
                        fontSize: '14px'
                      }
                    })
                  : React.createElement('div', {
                      style: {
                        background: 'var(--background)',
                        padding: '8px 12px',
                        borderRadius: '6px',
                        fontSize: '14px'
                      }
                    }, trans.translated_text || React.createElement('em', { className: 'text-secondary' }, 'No translation'))
              ),

              trans.error_message && React.createElement('div', {
                style: {
                  background: '#fef2f2',
                  color: '#dc2626',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  marginBottom: '12px'
                }
              }, 'Error: ' + trans.error_message),

              React.createElement('div', { className: 'flex gap-2 justify-end' },
                isEditing
                  ? React.createElement(React.Fragment, null,
                      React.createElement('button', {
                        className: 'btn btn-secondary',
                        onClick: function() { setEditing(null); }
                      }, 'Cancel'),
                      React.createElement('button', {
                        className: 'btn btn-primary',
                        onClick: function() { handleApprove(trans.id, editText); }
                      }, 'Save & Approve')
                    )
                  : React.createElement(React.Fragment, null,
                      trans.status === 'failed' && React.createElement('button', {
                        className: 'btn btn-secondary',
                        onClick: function() { handleRetry(trans.id); }
                      }, 'Retry'),
                      React.createElement('button', {
                        className: 'btn btn-secondary',
                        onClick: function() { startEdit(trans); }
                      }, 'Edit'),
                      React.createElement('button', {
                        className: 'btn btn-secondary',
                        style: { color: '#dc2626' },
                        onClick: function() { handleDelete(trans.id); }
                      }, 'Delete'),
                      trans.status !== 'approved' && React.createElement('button', {
                        className: 'btn btn-primary',
                        onClick: function() { handleApprove(trans.id); }
                      }, 'Approve')
                    )
              )
            );
          })
  );
}

export default AdminTranslations;
